/*************************************************************************
 * ChartComponent.jsx
 * Regenerated to accept a company URL parameter and route all endpoints to
 *   https://{company}.ogpumper.net  (or ogendeavors.com for "src")
 *
 * Added (per request):
 * - Pull pumper/user list from:   /api/userdetails.php
 * - Add "Pumper" dropdown that drives PumpersCsv=... in prod requests
 * - When PumpersCsv is set, production data is pulled via Rpt=EM (known to
 *   support PumpersCsv) and aggregated client-side by day/month so the chart
 *   still works for ~ALL~ leases.
 *
 * Layout safeguard:
 * - Lease/Well "nav" arrow buttons are kept INLINE with their dropdowns
 *   via .select-with-nav so they do not drop to the next line.
 *
 * Updates (12/2025 requests):
 * - Allow user to turn on/off series from Settings (series visibility toggles)
 * - Show user name in Settings title (Settings (lori)) using URL Name/name
 * - Don't include "Reset Legend" button on print
 * - Don't include inactive pumpers in the Pumper filter dropdown
 * - If user changes From/To dates, Time Range becomes "Custom"
 * - Fix print date off-by-one (YYYY-MM-DD parsing in local time)
 *************************************************************************/

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { SketchPicker } from "react-color";
import debounce from "lodash/debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRedo,
  faCog,
  faPrint,
  faChartBar,
  faChartLine,
  faTimes,
  faExchangeAlt,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

/* -------------------------------------------------------------
   URL-parameter helpers  (Name is sticky; never overwritten)
-------------------------------------------------------------- */

/* Read the ORIGINAL search once (so later replaceState calls can't drop Name) */
const INITIAL_QS = new URLSearchParams(window.location.search);

/* Track which key was used ("Name" or "name") and keep its exact value */
const stickyNameKey = INITIAL_QS.has("Name")
  ? "Name"
  : INITIAL_QS.has("name")
  ? "name"
  : null;

const urlName = stickyNameKey ? INITIAL_QS.get(stickyNameKey) : null; // keep as-is
const urlRpt = INITIAL_QS.get("Rpt") || INITIAL_QS.get("rpt") || "CD";

const normalizeQD = (v) => {
  if (!v) return "30";
  const s = String(v).trim();
  if (!s) return "30";
  return s.toLowerCase() === "custom" ? "Custom" : s;
};

const rawQD = INITIAL_QS.get("QD") || INITIAL_QS.get("qd") || "30";
const urlQD = normalizeQD(rawQD);

const urlLeaseID =
  INITIAL_QS.get("LeaseID") || INITIAL_QS.get("leaseid") || "~ALL~";
const urlFrom =
  INITIAL_QS.get("From") ||
  INITIAL_QS.get("FromDate") ||
  INITIAL_QS.get("from") ||
  INITIAL_QS.get("fromdate");
const urlThru =
  INITIAL_QS.get("Thru") ||
  INITIAL_QS.get("ThruDate") ||
  INITIAL_QS.get("thru") ||
  INITIAL_QS.get("thrudate");
const urlTag = INITIAL_QS.get("Tag") || INITIAL_QS.get("tag") || "All";
const urlUserID =
  INITIAL_QS.get("UserID") ||
  INITIAL_QS.get("userID") ||
  INITIAL_QS.get("userid");
const urlToken = INITIAL_QS.get("token");
const urlGrant = INITIAL_QS.get("grant") || INITIAL_QS.get("Grant"); // optional, preserved

/* NEW: PumpersCsv (pumper filter) */
const urlPumpersCsv =
  INITIAL_QS.get("PumpersCsv") ||
  INITIAL_QS.get("pumperscsv") ||
  INITIAL_QS.get("PUMPERCSV") ||
  "";

/* -------------------------------------------------------------
   Company detection → API base: https://{company}.ogpumper.net
-------------------------------------------------------------- */
function sanitizeCompany(raw) {
  if (!raw) return "";
  const v = String(raw).trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(v) ? v : "";
}

function detectCompany() {
  // 1) From query (read from ORIGINAL QS so it doesn't change under us)
  const qCompany =
    INITIAL_QS.get("Company") ||
    INITIAL_QS.get("company") ||
    INITIAL_QS.get("Subdomain") ||
    INITIAL_QS.get("subdomain") ||
    INITIAL_QS.get("c");
  if (qCompany) {
    const s = sanitizeCompany(qCompany);
    if (s) return s;
  }

  // 2) From path: /companyHome/:slug(/…)
  const parts = window.location.pathname.replace(/^\/+|\/+$/g, "").split("/");
  const idx = parts.indexOf("companyHome");
  if (idx !== -1 && parts[idx + 1]) {
    const s = sanitizeCompany(parts[idx + 1]);
    if (s) return s;
  }

  // 3) From host subdomain: foo.example.com → foo
  const hostParts = window.location.hostname.split(".");
  if (hostParts.length > 2) {
    const s = sanitizeCompany(hostParts[0]);
    if (s) return s;
  }

  // 4) Fallback default
  return "stasney";
}

const COMPANY = detectCompany();

/* Use ogendeavors.com for "src", otherwise ogpumper.net */
const API_BASE =
  COMPANY === "src"
    ? `https://${COMPANY}.ogendeavors.com`
    : `https://${COMPANY}.ogpumper.net`;

/* -------------------------------------------------------------
   Derive From/Thru dates from QD (mirrors original quick-link logic)
-------------------------------------------------------------- */
const deriveDates = (qd) => {
  const today = moment();
  switch (qd) {
    case "CM":
      return {
        from: today.clone().startOf("month").format("YYYY-MM-DD"),
        thru: today.clone().endOf("month").format("YYYY-MM-DD"),
      };
    case "3D":
      return {
        from: today.clone().subtract(2, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "7D":
      return {
        from: today.clone().subtract(6, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "LM":
      return {
        from: today
          .clone()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD"),
        thru: today
          .clone()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD"),
      };
    case "3M":
      return {
        from: today
          .clone()
          .subtract(3, "months")
          .add(1, "day")
          .format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "6M":
      return {
        from: today
          .clone()
          .subtract(6, "months")
          .add(1, "day")
          .format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "CY":
      return {
        from: today.clone().startOf("year").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
    case "LY":
      return {
        from: today
          .clone()
          .subtract(1, "year")
          .startOf("year")
          .format("YYYY-MM-DD"),
        thru: today
          .clone()
          .subtract(1, "year")
          .endOf("year")
          .format("YYYY-MM-DD"),
      };
    case "30":
    default:
      return {
        from: today.clone().subtract(29, "days").format("YYYY-MM-DD"),
        thru: today.clone().format("YYYY-MM-DD"),
      };
  }
};

/* -------------------------------------------------------------
   Initial From/Thru (URL overrides derived defaults)
-------------------------------------------------------------- */
const qdForDefaults = urlQD === "Custom" ? "30" : urlQD;
const dateDefaults = deriveDates(qdForDefaults);
const initialFrom = urlFrom || dateDefaults.from;
const initialThru = urlThru || dateDefaults.thru;

const initialQuickLink =
  urlQD === "Custom"
    ? "Custom"
    : (urlFrom || urlThru) &&
      ((urlFrom && urlFrom !== dateDefaults.from) ||
        (urlThru && urlThru !== dateDefaults.thru))
    ? "Custom"
    : urlQD;

/* -------------------------------------------------------------
   Helper: remove <=0 values when in log mode
-------------------------------------------------------------- */
const filterLogData = (data) =>
  data.map((d) => ({
    ...d,
    TestGas: d.TestGas > 0 ? d.TestGas : null,
    JTF: d.JTF > 0 ? d.JTF : null,
    FTF: d.FTF > 0 ? d.FTF : null,
    FAP: d.FAP > 0 ? d.FAP : null,
    GFFAP: d.GFFAP > 0 ? d.GFFAP : null,
    SND: d.SND > 0 ? d.SND : null,
  }));

/* =============================================================
   MAIN COMPONENT
============================================================= */
const ChartComponent = () => {
  /* -----------------------------------------------------------
     User / URL-param initialisation
  ------------------------------------------------------------ */
  const userID = urlUserID;

  /* -----------------------------------------------------------
     State
  ------------------------------------------------------------ */
  const [reportType, setReportType] = useState(urlRpt);
  const [quickLink, setQuickLink] = useState(initialQuickLink);
  const [selectedLeaseID, setSelectedLeaseID] = useState(urlLeaseID);
  const [fromDate, setFromDate] = useState(initialFrom);
  const [thruDate, setThruDate] = useState(initialThru);
  const [selectedTag, setSelectedTag] = useState(urlTag);

  /* NEW: Pumper filter (drives PumpersCsv) */
  const [selectedPumperCsv, setSelectedPumperCsv] = useState(urlPumpersCsv);
  const [pumpers, setPumpers] = useState([]);
  const [pumpersLoaded, setPumpersLoaded] = useState(false);

  const [selectedWellID, setSelectedWellID] = useState("");
  const [wells, setWells] = useState([]);
  const [leases, setLeases] = useState([]);
  const [tags, setTags] = useState(["All"]);
  const [chartView, setChartView] = useState("production");

  const chartRef = useRef(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /* ---- chartTypes, colours, prefs, etc. (unchanged) ---------- */
  const [chartTypes, setChartTypes] = useState({
    Oil: "bar",
    ProducedWater: "bar",
    InjectedWater: "bar",
    Gas: "line",
    Tbg: "line",
    Csg: "line",
    TestOil: "bar",
    TestWater: "bar",
    TestGas: "line",
    JTF: "line",
    FTF: "line",
    FAP: "line",
    GFFAP: "line",
    SND: "line",
  });
  const [colors, setColors] = useState({
    Oil: "#FF7F0E",
    ProducedWater: "#2CA02C",
    InjectedWater: "#1F77B4",
    Tbg: "#D62728",
    Csg: "#9467BD",
    Gas: "#8C564B",
    TestOil: "#FF7F0E",
    TestWater: "#2CA02C",
    TestGas: "#8C564B",
    JTF: "#FFBB78",
    FTF: "#98DF8A",
    FAP: "#AEC7E8",
    GFFAP: "#FF9896",
    SND: "#C5B0D5",
  });
  const [colorPicker, setColorPicker] = useState({
    visible: false,
    field: null,
  });
  const [logarithmic, setLogarithmic] = useState(false);
  const [stacked, setStacked] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [disabledSeries, setDisabledSeries] = useState([]);

  const displayNameForSettings =
    (userID ? String(userID).trim() : "") ||
    (urlName && String(urlName).trim());

  const seriesKeys =
    chartView === "production"
      ? ["Oil", "ProducedWater", "InjectedWater", "Gas", "Tbg", "Csg"]
      : [
          "TestOil",
          "TestWater",
          "TestGas",
          "Tbg",
          "Csg",
          "JTF",
          "FTF",
          "FAP",
          "GFFAP",
          "SND",
        ];

  /* -----------------------------------------------------------
     Sync state → URL bar (preserve current company & params)
  ------------------------------------------------------------ */
  useEffect(() => {
    const url = new URL(window.location.href);
    const qs = url.searchParams;

    // helper: only set non-empty values
    const set = (k, v) => {
      if (v === undefined || v === null || v === "") return;
      qs.set(k, String(v));
    };

    // update only the keys this component owns
    set("Rpt", reportType);
    set("QD", quickLink); // can be "Custom" for display/share
    set("LeaseID", selectedLeaseID);
    set("From", fromDate);
    set("Thru", thruDate);
    set("Tag", selectedTag);
    if (userID) set("UserID", userID);

    // NEW: PumpersCsv (allow delete when cleared)
    if (selectedPumperCsv) qs.set("PumpersCsv", selectedPumperCsv);
    else qs.delete("PumpersCsv");

    // keep company visible
    set("Company", COMPANY);

    // preserve token/grant from the original URL (no-op if already present)
    if (urlToken) qs.set("token", urlToken);
    if (urlGrant) qs.set("grant", urlGrant);

    // preserve sticky Name with original casing if it existed
    if (stickyNameKey && urlName && !qs.has(stickyNameKey)) {
      qs.set(stickyNameKey, urlName);
    }

    // IMPORTANT: don't touch other params (e.g., App, custom flags)
    window.history.replaceState(
      null,
      "",
      `${url.pathname}?${qs.toString()}${url.hash}`
    );
  }, [
    reportType,
    quickLink,
    selectedLeaseID,
    fromDate,
    thruDate,
    selectedTag,
    userID,
    selectedPumperCsv,
  ]);

  /* -----------------------------------------------------------
     Fetch user prefs
  ------------------------------------------------------------ */
  const fetchPreferences = useCallback(async () => {
    // If we don't know which user this is, we can't fetch or apply prefs.
    if (!userID) {
      console.warn(
        "fetchPreferences: missing userID; skipping ChartsPref fetch."
      );
      return;
    }
    try {
      const url = `${API_BASE}/api/userdetails.php?id=${userID}&chartsPref=true`;
      const resp = await fetch(url, {
        // Pass token as Authorization: Bearer … if we have one
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      const json = await resp.json();
      if (json.success && json.ChartsPref) {
        const { chartTypes, colors, logarithmic, disabledSeries } =
          json.ChartsPref;
        setChartTypes({ ...chartTypes });
        setColors({ ...colors });
        setLogarithmic(!!logarithmic);
        setDisabledSeries(disabledSeries || []);
      }
    } catch (e) {
      console.error("fetchPreferences:", e);
    }
  }, [userID, urlToken]);

  /* -----------------------------------------------------------
     Fetch pumpers/users for PumpersCsv dropdown
     - Only active pumpers (exclude inactive/Disabled)
  ------------------------------------------------------------ */
  const fetchPumpers = useCallback(async () => {
    setPumpersLoaded(false);

    // Disabled can be 0/1 (or "0"/"1"). Treat ONLY 1/"1"/true as disabled.
    const isDisabled = (v) => v === 1 || v === "1" || v === true;

    // Hardened "bad/placeholder" IDs (case-insensitive)
    const norm = (s) => String(s ?? "").trim();
    const isBadUserId = (id) => {
      const v = norm(id).toLowerCase();
      return (
        v === "" ||
        v === "unassigned" ||
        v === "(unassigned)" ||
        v === "inactive" ||
        v === "disabled" ||
        v === "none" ||
        v === "null" ||
        v === "undefined"
      );
    };

    // (Optional extra hardening) also exclude records whose FullName is a placeholder
    const isBadName = (name) => {
      const v = norm(name).toLowerCase();
      return (
        v === "unassigned" ||
        v === "(unassigned)" ||
        v === "inactive" ||
        v === "disabled"
      );
    };

    try {
      const resp = await fetch(`${API_BASE}/api/userdetails.php`, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) throw new Error("Network response was not ok");
      const json = await resp.json();

      const users = Array.isArray(json?.users) ? json.users : [];
      const pumperUsers = users
        .filter((u) => u && !isBadUserId(u.UserID)) // exclude "unassigned" and other placeholders
        .filter((u) => (u.Role || "").toUpperCase() === "P")
        .filter((u) => !isDisabled(u.Disabled)) // exclude disabled (1/"1"/true)
        .filter((u) => !isBadName(u.FullName)) // extra guard against placeholder names
        .sort((a, b) => {
          const an = (a.FullName || a.UserID || "").toLowerCase();
          const bn = (b.FullName || b.UserID || "").toLowerCase();
          return an.localeCompare(bn);
        });

      setPumpers(pumperUsers);
    } catch (e) {
      console.error("fetchPumpers:", e);
      setPumpers([]);
    } finally {
      setPumpersLoaded(true);
    }
  }, [urlToken]);

  // If URL/prefs had an inactive/unknown pumper, clear the selection once pumpers are loaded
  useEffect(() => {
    if (!pumpersLoaded) return;
    const val = String(selectedPumperCsv || "").trim();
    if (!val) return;
    const exists = pumpers.some((p) => String(p.UserID) === val);
    if (!exists) setSelectedPumperCsv("");
  }, [pumpersLoaded, pumpers, selectedPumperCsv]);

  /* -----------------------------------------------------------
     Fetch leases + tags
  ------------------------------------------------------------ */
  const fetchLeases = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/leases.php?tokdebug=1`, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) throw new Error("Network response was not ok");
      const leaseData = await resp.json();

      setLeases(leaseData);
      setWells(
        leaseData.flatMap((lease) =>
          lease.Wells.map((w) => ({ ...w, LeaseName: lease.LeaseName }))
        )
      );
      const uniqueTags = [
        ...new Set(
          leaseData.flatMap((l) =>
            [l.Tag1, l.Tag2, l.Tag3, l.Tag4].filter(Boolean)
          )
        ),
      ];
      setTags(["All", ...uniqueTags]);
    } catch (e) {
      console.error("fetchLeases:", e);
    }
  }, [urlToken]);

  /* -----------------------------------------------------------
     Fetch production / well-test data
  ------------------------------------------------------------ */
  const fetchChartData = useCallback(async () => {
    try {
      // If a pumper filter is chosen, use Rpt=EM (known to support PumpersCsv)
      const usePumperFilter =
        selectedPumperCsv !== undefined &&
        selectedPumperCsv !== null &&
        String(selectedPumperCsv).trim() !== "";

      const rptForEndpoint = usePumperFilter ? "EM" : reportType;

      // Keep QD stable for backend; "Custom" is a UI concept.
      const qdForEndpoint = quickLink === "Custom" ? "30" : quickLink;

      // NEW: when using EM + PumpersCsv, force "primary-only" (PumperID only)
      // (prevents default behavior of PumperID OR ReliefID)
      const emPrimaryOnlyParam = usePumperFilter
        ? `&EMPumperScope=PUMPER_ONLY`
        : "";

      const url =
        `${API_BASE}/service_testprod.php?Rpt=${encodeURIComponent(
          rptForEndpoint
        )}` +
        `&QD=${encodeURIComponent(qdForEndpoint)}` +
        `&LeaseID=${encodeURIComponent(selectedLeaseID)}` +
        `&From=${fromDate}` +
        `&Thru=${thruDate}` +
        `&Tag=${encodeURIComponent(selectedTag)}` +
        (usePumperFilter
          ? `&PumpersCsv=${encodeURIComponent(
              String(selectedPumperCsv).trim()
            )}`
          : "") +
        emPrimaryOnlyParam;

      const resp = await fetch(url, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) {
        throw new Error("Network response was not ok");
      }
      const json = await resp.json();
      const rows = Array.isArray(json) ? json : [];

      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };

      // Normalize rows from either the original chart report OR EM rows.
      // Then aggregate by date/month so the chart always has one entry per x-axis bucket.
      const normalized = rows.map((i) => {
        const rawGauge = i.GaugeDate;

        const gaugeKey =
          reportType === "CM"
            ? // Monthly view:
              // - If backend gave MonthYear (non-EM path), use it
              // - If EM path, derive MM-YYYY from GaugeDate
              i.MonthYear
              ? i.MonthYear
              : rawGauge
              ? moment(rawGauge).format("MM-YYYY")
              : ""
            : // Daily view: normalize to YYYY-MM-DD
            rawGauge
            ? moment(rawGauge).format("YYYY-MM-DD")
            : "";

        return {
          GaugeDate: gaugeKey,
          Oil: toNum(i.Produced),
          ProducedWater: toNum(i.ProducedWaterTotal),
          InjectedWater: toNum(i.InjectedWaterTotal),
          Gas: toNum(i.Gas),
          // EM rows use lowercase tbg/csg; chart rows often use Tbg/Csg
          Tbg: toNum(i.Tbg ?? i.tbg),
          Csg: toNum(i.Csg ?? i.csg),
        };
      });

      // Aggregate by GaugeDate (sum values)
      const byKey = new Map();
      for (const r of normalized) {
        if (!r.GaugeDate) continue;
        const prev = byKey.get(r.GaugeDate);
        if (!prev) {
          byKey.set(r.GaugeDate, { ...r });
        } else {
          prev.Oil += r.Oil;
          prev.ProducedWater += r.ProducedWater;
          prev.InjectedWater += r.InjectedWater;
          prev.Gas += r.Gas;
          prev.Tbg += r.Tbg;
          prev.Csg += r.Csg;
        }
      }

      const result = Array.from(byKey.values());

      // Sort x-axis buckets
      const keyToTime = (k) =>
        reportType === "CM"
          ? moment(k, "MM-YYYY", true).valueOf()
          : moment(k, "YYYY-MM-DD", true).valueOf();

      result.sort((a, b) => keyToTime(a.GaugeDate) - keyToTime(b.GaugeDate));

      return result;
    } catch (e) {
      console.error("fetchChartData:", e);
      return [];
    }
  }, [
    reportType,
    quickLink,
    selectedLeaseID,
    fromDate,
    thruDate,
    selectedTag,
    selectedPumperCsv,
    urlToken,
  ]);

  const fetchWellTestData = useCallback(async () => {
    try {
      const url =
        `${API_BASE}/service_welltests.php?LeaseID=${selectedLeaseID}` +
        `&WellID=${selectedWellID}&FromDate=${fromDate}&ThruDate=${thruDate}`;
      const resp = await fetch(url, {
        headers: urlToken ? { Authorization: `Bearer ${urlToken}` } : undefined,
      });
      if (!resp.ok) throw new Error("Network response was not ok");
      const json = await resp.json();
      return json.map((i) => ({
        GaugeDate: i.GaugeDate,
        TestOil: parseFloat(i.TestOil) || 0,
        TestWater: parseFloat(i.TestWater) || 0,
        TestGas: parseFloat(i.TestGas) || 0,
        Tbg: parseFloat(i.TbgPressure) || 0,
        Csg: parseFloat(i.CsgPressure) || 0,
        JTF: parseFloat(i.JTF) || 0,
        FTF: parseFloat(i.FTF) || 0,
        FAP: parseFloat(i.FAP) || 0,
        GFFAP: parseFloat(i.GFFAP) || 0,
        SND: parseFloat(i.SNDepth) || 0,
      }));
    } catch (e) {
      console.error("fetchWellTestData:", e);
      return [];
    }
  }, [selectedLeaseID, selectedWellID, fromDate, thruDate, urlToken]);

  /* -----------------------------------------------------------
     On-mount fetches
  ------------------------------------------------------------ */
  useEffect(() => {
    fetchPreferences();
    fetchLeases();
    fetchPumpers();
  }, [fetchPreferences, fetchLeases, fetchPumpers]);

  /* -----------------------------------------------------------
     Fetch data when dependencies change
  ------------------------------------------------------------ */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const result =
          chartView === "production"
            ? await fetchChartData()
            : await fetchWellTestData();
        if (mounted) {
          setData(result);
          setIsLoading(false);
        }
      } catch (e) {
        console.error("fetchData:", e);
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [chartView, fetchChartData, fetchWellTestData]);

  /* -----------------------------------------------------------
     Debounced preference saver
  ------------------------------------------------------------ */
  const debouncedSavePreferences = useCallback(
    debounce(async () => {
      // Don’t try to save if we don’t know which user this is
      if (!userID) {
        console.warn("savePreferences: missing userID; skipping PATCH.");
        return;
      }
      try {
        const headers = { "Content-Type": "application/json" };
        if (urlToken) headers.Authorization = `Bearer ${urlToken}`;

        const resp = await fetch(`${API_BASE}/api/userdetails.php?tokdebug=1`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            UserID: userID,
            ChartsPref: { chartTypes, colors, logarithmic, disabledSeries },
          }),
        });
        const json = await resp.json();
        if (!json.success) console.error("savePrefs:", json.message);
      } catch (e) {
        console.error("savePrefs:", e);
      }
    }, 300),
    [userID, chartTypes, colors, logarithmic, disabledSeries, urlToken]
  );

  useEffect(() => {
    debouncedSavePreferences();
  }, [
    chartTypes,
    colors,
    logarithmic,
    disabledSeries,
    debouncedSavePreferences,
  ]);

  /* -----------------------------------------------------------
     QuickLink handler (updates dates & state)
     - If user selects Custom: do not overwrite dates
  ------------------------------------------------------------ */
  const handleQuickLinkChange = (qd) => {
    if (qd === "Custom") {
      setQuickLink("Custom");
      return;
    }
    setQuickLink(qd);
    const { from, thru } = deriveDates(qd);
    setFromDate(from);
    setThruDate(thru);
  };

  const handleFromDateChange = (v) => {
    setFromDate(v);
    setQuickLink("Custom");
  };

  const handleThruDateChange = (v) => {
    setThruDate(v);
    setQuickLink("Custom");
  };

  /* -----------------------------------------------------------
     Remaining handlers (toggle, legend, colour, navigate, etc.)
  ------------------------------------------------------------ */
  const handleToggle = (field) =>
    setChartTypes((p) => ({
      ...p,
      [field]: p[field] === "line" ? "bar" : "line",
    }));

  const handleColorChange = (c) => {
    setColors((p) => ({ ...p, [colorPicker.field]: c.hex }));
    setColorPicker({ visible: false, field: null });
  };

  const handleLegendClick = ({ dataKey }) =>
    setDisabledSeries((p) =>
      p.includes(dataKey) ? p.filter((i) => i !== dataKey) : [...p, dataKey]
    );

  const resetLegend = () => setDisabledSeries([]);

  const setSeriesVisible = (key, isVisible) => {
    setDisabledSeries((prev) => {
      const has = prev.includes(key);
      if (isVisible) {
        return has ? prev.filter((k) => k !== key) : prev;
      }
      return has ? prev : [...prev, key];
    });
  };

  const renderLegendText = (v, entry) => {
    const { color } = entry;
    let unit = "BBLS";
    if (
      v === "Gas" ||
      v === "TestGas" ||
      v === "JTF" ||
      v === "FTF" ||
      v === "FAP" ||
      v === "GFFAP" ||
      v === "SND"
    )
      unit = "MCF";
    return (
      <span style={{ color: disabledSeries.includes(v) ? "#ccc" : color }}>
        {v} ({unit})
      </span>
    );
  };

  useEffect(() => {
    if (selectedLeaseID !== "~ALL~") {
      const lease = leases.find((l) => l.LeaseID === selectedLeaseID);
      if (lease) {
        setWells(lease.Wells);
        setSelectedWellID(lease.Wells.length ? lease.Wells[0].WellID : "");
      }
    } else {
      setWells(
        leases.flatMap((l) =>
          l.Wells.map((w) => ({ ...w, LeaseName: l.LeaseName }))
        )
      );
      setSelectedWellID("");
    }
  }, [selectedLeaseID, leases]);

  const handleNextLease = () => {
    const idx = leases.findIndex((l) => l.LeaseID === selectedLeaseID);
    const next = (idx + 1) % leases.length;
    setSelectedLeaseID(leases[next].LeaseID);
  };

  const handleNextWell = () => {
    const idx = wells.findIndex((w) => w.WellID === selectedWellID);
    const next = (idx + 1) % wells.length;
    setSelectedWellID(wells[next].WellID);
  };

  const handlePrint = () => {
    const html = chartRef.current.innerHTML;
    const original = document.body.innerHTML;
    const leaseName =
      leases.find((l) => l.LeaseID === selectedLeaseID)?.LeaseName ||
      "All Leases";

    // FIX: parse YYYY-MM-DD as local date (avoid UTC off-by-one)
    const fmt = (d) => {
      const m = moment(d, "YYYY-MM-DD", true);
      return m.isValid() ? m.format("M/D/YY") : String(d || "");
    };

    const pumperLabel =
      selectedPumperCsv && String(selectedPumperCsv).trim()
        ? pumpers.find((p) => String(p.UserID) === String(selectedPumperCsv))
            ?.FullName || selectedPumperCsv
        : "";

    document.body.innerHTML = `
      <style>
        @media print{
          @page{size:landscape;margin:.5cm;}
          body{margin:0;font-family:Arial}
          /* hide UI elements that should not print */
          .reset-legend-button{display:none !important;}
          .fab-container{display:none !important;}
          .settings-panel{display:none !important;}
          .filters-container{display:none !important;}
          .color-picker-modal{display:none !important;}
          button{display:none !important;}
        }
      </style>
      <h1>${chartView === "production" ? "Production" : "Well Test"} Chart</h1>
      <p><b>Date Range:</b> ${fmt(fromDate)} to ${fmt(thruDate)}</p>
      <p><b>Lease:</b> ${leaseName}</p>
      ${
        chartView === "production" && selectedTag !== "All"
          ? `<p><b>Tag:</b> ${selectedTag}</p>`
          : ""
      }
      ${
        chartView === "production" && pumperLabel
          ? `<p><b>Pumper:</b> ${pumperLabel}</p>`
          : ""
      }
      ${
        chartView === "wellTest"
          ? `<p><b>Well:</b> ${selectedWellID || "All Wells"}</p>`
          : ""
      }
      <p><b>Y-Axis:</b> ${logarithmic ? "Logarithmic" : "Linear"}</p>
      ${html}
    `;
    window.print();
    document.body.innerHTML = original;
    window.location.reload();
  };

  const handleEditClick = () => {
    setIsSidePanelOpen((o) => !o);
    setIsEditing((o) => !o);
  };

  const handleChartViewChange = () => {
    setChartView((v) => (v === "production" ? "wellTest" : "production"));
    setData([]);
  };

  /* -----------------------------------------------------------
   * HTML / JSX SEGMENT
   * (Updated to add: Custom time range, active-only pumpers,
   *  series visibility toggles, Settings title w/ Name)
   ------------------------------------------------------------ */
  return (
    <>
      <div className="chart-container">
        <div className={`filters-container ${isEditing ? "editing" : ""}`}>
          <div className="filters">
            <div className="filter-group">
              <div className="filter-item">
                <label>Time Range</label>
                <select
                  value={quickLink}
                  onChange={(e) => handleQuickLinkChange(e.target.value)}
                >
                  <option value="30">Last 30 days</option>
                  <option value="CM">Current Month</option>
                  <option value="3D">Last 3 Days</option>
                  <option value="7D">Last 7 Days</option>
                  <option value="LM">Last Month</option>
                  <option value="3M">Last 3 months</option>
                  <option value="6M">Last 6 months</option>
                  <option value="CY">Current Year</option>
                  <option value="LY">Last Year</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>
              <div className="filter-item">
                <label>From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                />
              </div>
              <div className="filter-item">
                <label>To</label>
                <input
                  type="date"
                  value={thruDate}
                  onChange={(e) => handleThruDateChange(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label>Lease</label>
                <div className="select-with-nav">
                  <select
                    value={selectedLeaseID}
                    onChange={(e) => setSelectedLeaseID(e.target.value)}
                  >
                    {chartView === "production" ? (
                      <option value="~ALL~">All Leases</option>
                    ) : (
                      leases.length > 0 && (
                        <option
                          value={leases[0].LeaseID}
                          key={leases[0].LeaseID}
                        >
                          {leases[0].LeaseName}
                        </option>
                      )
                    )}
                    {leases
                      .filter(
                        (lease) =>
                          chartView === "production" || lease.WellType !== "INJ"
                      )
                      .map((lease, index) => (
                        <option
                          key={lease.LeaseID}
                          value={lease.LeaseID}
                          selected={chartView !== "production" && index === 0}
                        >
                          {lease.LeaseName}
                        </option>
                      ))}
                  </select>
                  <button
                    className="next-well-button"
                    onClick={handleNextLease}
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>

              {chartView === "production" ? (
                <div className="filter-item">
                  <label>Tag</label>
                  <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                  >
                    {tags.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="filter-item">
                  <label>Well</label>
                  <div className="select-with-nav">
                    <select
                      value={selectedWellID}
                      onChange={(e) => setSelectedWellID(e.target.value)}
                      disabled={selectedLeaseID === "~ALL~"}
                    >
                      <option value="">Select a well</option>
                      {wells.map((well) => (
                        <option key={well.UniqID} value={well.WellID}>
                          {well.WellID}
                        </option>
                      ))}
                    </select>
                    <button
                      className="next-well-button"
                      onClick={handleNextWell}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </div>
              )}

              {chartView === "production" && (
                <div className="filter-item">
                  <label>Pumper</label>
                  <select
                    value={selectedPumperCsv}
                    onChange={(e) => setSelectedPumperCsv(e.target.value)}
                  >
                    <option value="">All Pumpers</option>
                    {pumpers.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.FullName && u.FullName.trim()
                          ? u.FullName
                          : u.UserID}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {chartView === "production" && (
                <div className="filter-item">
                  <label>Report Type</label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <option value="CD">Daily</option>
                    <option value="CM">Monthly</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="action-buttons">
            <button
              className="action-button print-button"
              onClick={handlePrint}
            >
              <FontAwesomeIcon icon={faPrint} className="icon" /> Print
            </button>
            <button
              className="action-button settings-button"
              onClick={handleEditClick}
            >
              <FontAwesomeIcon
                icon={isSidePanelOpen ? faTimes : faCog}
                className="icon"
              />
              {isSidePanelOpen ? "Done" : "Edit"}
            </button>
          </div>
        </div>

        <div ref={chartRef} className="chart-container-inner">
          {isLoading ? (
            <div className="loading-spinner"></div>
          ) : (
            <ResponsiveContainer width="100%" height={700} key={chartView}>
              <ComposedChart data={logarithmic ? filterLogData(data) : data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="GaugeDate"
                  tickFormatter={(t) =>
                    reportType === "CM"
                      ? moment(t, "MM-YYYY").format("M/YY")
                      : moment(t, "YYYY-MM-DD").format("M/D/YY")
                  }
                  interval="preserveStartEnd"
                  tick={{ fontSize: 12, angle: -45, textAnchor: "end" }}
                  height={60}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis
                  yAxisId="left"
                  scale={logarithmic ? "log" : "linear"}
                  domain={logarithmic ? [1, "auto"] : [0, (max) => max * 1.1]}
                  allowDataOverflow
                  label={{ value: "BBLS", angle: -90, position: "insideLeft" }}
                  tickCount={logarithmic ? undefined : 7}
                  tickFormatter={(v) =>
                    logarithmic
                      ? v >= 1e6
                        ? `${(v / 1e6).toFixed(1)}M`
                        : v >= 1e3
                        ? `${(v / 1e3).toFixed(1)}K`
                        : v.toFixed(1)
                      : v >= 1e6
                      ? `${(v / 1e6).toFixed(1)}M`
                      : v >= 1e3
                      ? `${(v / 1e3).toFixed(1)}K`
                      : v.toFixed(1)
                  }
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  scale={logarithmic ? "log" : "linear"}
                  domain={logarithmic ? [1, "auto"] : [0, (max) => max * 1.1]}
                  allowDataOverflow
                  label={{ value: "MCF", angle: -90, position: "insideRight" }}
                  tickCount={logarithmic ? undefined : 7}
                  tickFormatter={(v) =>
                    logarithmic
                      ? v >= 1e6
                        ? `${(v / 1e6).toFixed(1)}M`
                        : v >= 1e3
                        ? `${(v / 1e3).toFixed(1)}K`
                        : v.toFixed(1)
                      : v >= 1e6
                      ? `${(v / 1e6).toFixed(1)}M`
                      : v >= 1e3
                      ? `${(v / 1e3).toFixed(1)}K`
                      : v.toFixed(1)
                  }
                />
                <Tooltip />
                <Legend
                  onClick={handleLegendClick}
                  formatter={renderLegendText}
                />
                {/* === SERIES RENDERING (unchchanged) ================== */}
                {chartView === "production" ? (
                  <>
                    {!disabledSeries.includes("Oil") &&
                      (chartTypes.Oil === "bar" ? (
                        <Bar yAxisId="left" dataKey="Oil" fill={colors.Oil} />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="Oil"
                          stroke={colors.Oil}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("ProducedWater") &&
                      (chartTypes.ProducedWater === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="ProducedWater"
                          fill={colors.ProducedWater}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="ProducedWater"
                          stroke={colors.ProducedWater}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("InjectedWater") &&
                      (chartTypes.InjectedWater === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="InjectedWater"
                          fill={colors.InjectedWater}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="InjectedWater"
                          stroke={colors.InjectedWater}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Gas") &&
                      (chartTypes.Gas === "bar" ? (
                        <Bar yAxisId="right" dataKey="Gas" fill={colors.Gas} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Gas"
                          stroke={colors.Gas}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Tbg") &&
                      (chartTypes.Tbg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Tbg" fill={colors.Tbg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Tbg"
                          stroke={colors.Tbg}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Csg") &&
                      (chartTypes.Csg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Csg" fill={colors.Csg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Csg"
                          stroke={colors.Csg}
                          dot={false}
                        />
                      ))}
                  </>
                ) : (
                  <>
                    {!disabledSeries.includes("TestOil") &&
                      (chartTypes.TestOil === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="TestOil"
                          name="Test Oil"
                          fill={colors.TestOil}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="TestOil"
                          name="Test Oil"
                          stroke={colors.TestOil}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("TestWater") &&
                      (chartTypes.TestWater === "bar" ? (
                        <Bar
                          yAxisId="left"
                          dataKey="TestWater"
                          name="Test Water"
                          fill={colors.TestWater}
                        />
                      ) : (
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="TestWater"
                          name="Test Water"
                          stroke={colors.TestWater}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("TestGas") &&
                      (chartTypes.TestGas === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="TestGas"
                          name="Test Gas"
                          fill={colors.TestGas}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="TestGas"
                          name="Test Gas"
                          stroke={colors.TestGas}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Tbg") &&
                      (chartTypes.Tbg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Tbg" fill={colors.Tbg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Tbg"
                          stroke={colors.Tbg}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("Csg") &&
                      (chartTypes.Csg === "bar" ? (
                        <Bar yAxisId="right" dataKey="Csg" fill={colors.Csg} />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="Csg"
                          stroke={colors.Csg}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("JTF") &&
                      (chartTypes.JTF === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="JTF"
                          name="JTF"
                          fill={colors.JTF}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="JTF"
                          name="JTF"
                          stroke={colors.JTF}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("FTF") &&
                      (chartTypes.FTF === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="FTF"
                          name="FTF"
                          fill={colors.FTF}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="FTF"
                          name="FTF"
                          stroke={colors.FTF}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("FAP") &&
                      (chartTypes.FAP === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="FAP"
                          name="FAP"
                          fill={colors.FAP}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="FAP"
                          name="FAP"
                          stroke={colors.FAP}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("GFFAP") &&
                      (chartTypes.GFFAP === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="GFFAP"
                          name="GFFAP"
                          fill={colors.GFFAP}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="GFFAP"
                          name="GFFAP"
                          stroke={colors.GFFAP}
                          dot={false}
                        />
                      ))}
                    {!disabledSeries.includes("SND") &&
                      (chartTypes.SND === "bar" ? (
                        <Bar
                          yAxisId="right"
                          dataKey="SND"
                          name="SND"
                          fill={colors.SND}
                        />
                      ) : (
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="SND"
                          name="SND"
                          stroke={colors.SND}
                          dot={false}
                        />
                      ))}
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <button className="reset-legend-button" onClick={resetLegend}>
            <FontAwesomeIcon icon={faRedo} className="icon" /> Reset Legend
          </button>
        </div>

        <div className={`settings-panel ${isSidePanelOpen ? "open" : ""}`}>
          <h2>
            Settings
            {displayNameForSettings ? ` (${displayNameForSettings})` : ""}
          </h2>

          <div className="settings-section">
            <h3>Series Visibility</h3>
            {seriesKeys.map((k) => (
              <label key={k} className="checkbox-label series-checkbox">
                <input
                  type="checkbox"
                  checked={!disabledSeries.includes(k)}
                  onChange={(e) => setSeriesVisible(k, e.target.checked)}
                />
                {k}
              </label>
            ))}
          </div>

          <div className="settings-section">
            <h3>Chart Type and Color</h3>
            {Object.entries(
              chartView === "production"
                ? {
                    Oil: chartTypes.Oil,
                    ProducedWater: chartTypes.ProducedWater,
                    InjectedWater: chartTypes.InjectedWater,
                    Gas: chartTypes.Gas,
                    Tbg: chartTypes.Tbg,
                    Csg: chartTypes.Csg,
                  }
                : {
                    TestOil: chartTypes.TestOil,
                    TestWater: chartTypes.TestWater,
                    TestGas: chartTypes.TestGas,
                    Tbg: chartTypes.Tbg,
                    Csg: chartTypes.Csg,
                    JTF: chartTypes.JTF,
                    FTF: chartTypes.FTF,
                    FAP: chartTypes.FAP,
                    GFFAP: chartTypes.GFFAP,
                    SND: chartTypes.SND,
                  }
            ).map(([field, type]) => (
              <div key={field} className="settings-item">
                <span>{field}</span>
                <div className="chart-controls">
                  <button
                    className="chart-type-button"
                    onClick={() => handleToggle(field)}
                  >
                    {type === "line" ? (
                      <FontAwesomeIcon icon={faChartBar} />
                    ) : (
                      <FontAwesomeIcon icon={faChartLine} />
                    )}
                  </button>
                  <div
                    className="color-box"
                    style={{ backgroundColor: colors[field] }}
                    onClick={() => setColorPicker({ visible: true, field })}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="settings-section">
            <h3>Y-Axis Scale</h3>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={logarithmic}
                onChange={() => setLogarithmic(!logarithmic)}
              />
              Logarithmic
            </label>
          </div>

          {chartView === "production" && (
            <div className="settings-section">
              <h3>Stacked Options</h3>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={stacked}
                  onChange={() => setStacked(!stacked)}
                />
                Stacked Gas and Tbg
              </label>
            </div>
          )}
        </div>

        {colorPicker.visible && (
          <div className="color-picker-modal">
            <div className="color-picker-container">
              <SketchPicker
                color={colors[colorPicker.field]}
                onChangeComplete={handleColorChange}
              />
              <button
                className="close-button"
                onClick={() => setColorPicker({ visible: false, field: null })}
              >
                Close
              </button>
            </div>
          </div>
        )}

        <div className="fab-container">
          <button className="fab" onClick={handleChartViewChange}>
            <FontAwesomeIcon icon={faExchangeAlt} />
            {chartView === "production" ? "Wells" : "Prod"}
          </button>
        </div>
      </div>

      {/* ======= STYLE BLOCK (updated to keep nav buttons inline + fit new dropdown) ======= */}
      <style jsx>{`
        .chart-container {
          /* share panel width via CSS variable */
          --panel-width: 320px;

          min-height: 100vh;
          padding: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 0px;
          width: 100%;
          box-sizing: border-box;
        }

        .filters-container {
          width: 100%;
          max-width: 100%;
          background: linear-gradient(to right, #ebf4ff, #dee6ff);
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          padding: 8px;
          display: flex;
          align-items: center;
          transition: transform 0.3s ease-in-out, padding-right 0.3s ease;
          /* keep controls always visible & above panel */
          position: sticky;
          top: 0;
          z-index: 1200;
          backdrop-filter: saturate(1.1);
        }

        /* don't slide the filters left while editing.
           Instead, add right padding to make space for the panel (fallback if :has() unsupported). */
        .filters-container.editing {
          transform: none;
          padding-right: var(--panel-width);
        }

        .filters {
          flex: 1;
          display: flex;
          flex-wrap: nowrap; /* keep one row on desktop */
          align-items: center;
          min-width: 0; /* allow overflow control */
        }

        .filter-group {
          display: flex;
          flex-wrap: nowrap; /* keep one row on desktop */
          gap: 10px;
          flex: 1;
          min-width: 0;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 0;
          min-width: 130px; /* slightly smaller to fit the new Pumper filter */
        }

        .filter-item label {
          margin-bottom: 4px;
          font-size: 11px; /* slightly reduced font */
          font-weight: 500;
          color: #4a4a4a;
        }

        .filter-item select,
        .filter-item input {
          width: 100%;
          max-width: 150px; /* slightly reduced from 160 to prevent wrapping */
          padding: 4px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .filter-item select:focus,
        .filter-item input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
          outline: none;
        }

        /* KEEP NAV BUTTONS INLINE WITH THEIR SELECTS */
        .select-with-nav {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .select-with-nav select {
          flex: 1 1 auto;
          min-width: 0;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          margin-left: 16px;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .action-button {
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }

        .print-button {
          background-color: #10b981;
          color: #fff;
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
        }
        .print-button:hover {
          background-color: #059669;
        }

        .settings-button {
          background-color: #3b82f6;
          color: #fff;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
        }
        .settings-button:hover {
          background-color: #2563eb;
        }

        .next-lease-button,
        .next-well-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          margin-left: 0; /* handled by .select-with-nav gap */
          background-color: transparent;
          color: #1d72b8;
          border: none;
          cursor: pointer;
          transition: color 0.3s ease;
          flex: 0 0 auto;
        }
        .next-lease-button:hover,
        .next-well-button:hover {
          color: #155a8a;
        }
        .next-lease-button:focus,
        .next-well-button:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(29, 114, 184, 0.4);
        }
        .next-lease-button .fa-icon,
        .next-well-button .fa-icon {
          margin-left: 0;
        }

        .chart-container-inner {
          width: 100%;
          max-width: 100%;
          background-color: #ffffff;
          border-radius: 8px;
          padding: 32px;
          margin-bottom: 32px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: margin-right 0.3s ease; /* smooth shift when panel opens */
        }

        .loading-spinner {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 400px;
        }

        .loading-spinner::after {
          content: "";
          display: block;
          width: 64px;
          height: 64px;
          border: 8px solid #f3f4f6;
          border-top: 8px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .reset-legend-button {
          margin-top: 16px;
          padding: 8px 16px;
          background-color: #3b82f6;
          color: #fff;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .reset-legend-button:hover {
          background-color: #2563eb;
        }

        .settings-panel {
          position: fixed;
          /* MOVED LOWER THAN TOP MENU BAR (desktop) */
          top: 160px;
          height: auto;
          max-height: calc(100vh - 136px);
          overflow-y: auto;
          width: var(--panel-width);
          padding: 24px;
          background-color: #ffffff;
          border-left: 1px solid #e5e7eb;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          transition: right 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
          right: calc(var(--panel-width) * -1); /* hidden off-canvas */
          z-index: 1100; /* below filters + fab, above chart */
          -webkit-overflow-scrolling: touch;
          touch-action: pan-y;
        }
        .settings-panel.open {
          right: 0;
          box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
        }

        .settings-panel h2 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 16px;
        }
        .settings-section {
          margin-bottom: 24px;
        }
        .settings-section h3 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .settings-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .chart-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .chart-type-button {
          padding: 6px;
          border-radius: 4px;
          background-color: #3b82f6;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .chart-type-button:hover {
          background-color: #2563eb;
        }

        .color-box {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
        }
        .checkbox-label input {
          margin-right: 8px;
        }

        .series-checkbox {
          display: flex;
          align-items: center;
          margin: 6px 0;
          font-size: 13px;
          color: #374151;
          user-select: none;
        }

        .color-picker-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1300; /* above panel + fab for clarity when picking colors */
        }

        .color-picker-container {
          background-color: #ffffff;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .close-button {
          margin-top: 16px;
          padding: 8px 16px;
          background-color: #ef4444;
          color: #fff;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.15s ease, box-shadow 0.15s ease;
        }
        .close-button:hover {
          background-color: #dc2626;
        }

        .fab-container {
          position: fixed;
          bottom: 32px;
          right: 32px;
          z-index: 1250;
        }
        .fab {
          width: 56px;
          height: 56px;
          border-radius: 28px;
          background-color: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .fab:hover {
          background-color: #2563eb;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
        }

        /* when the panel opens, gently make room for it (supported in modern browsers) */
        .chart-container:has(.settings-panel.open) .chart-container-inner {
          margin-right: var(--panel-width);
        }
        .chart-container:has(.settings-panel.open) .filters-container {
          padding-right: var(--panel-width);
        }
        .settings-panel.open ~ .fab-container {
          right: calc(32px + var(--panel-width));
        }

        /* ---------- Responsive fallbacks ---------- */
        @media (max-width: 1024px) {
          .filters {
            flex-wrap: wrap;
          }
          .filter-group {
            flex-wrap: wrap;
            gap: 12px;
          }
          .filter-item {
            min-width: 180px;
          }
          .filter-item select,
          .filter-item input {
            max-width: 200px;
          }

          /* Mobile/tablet panel sizing that never covers the sticky bar */
          .settings-panel {
            top: 112px; /* lower than top bar on tablet/mobile */
            width: 88vw;
            right: -88vw;
            max-height: calc(100vh - 128px);
          }
          .settings-panel.open {
            right: 0;
          }
          /* Make room for the panel on smaller screens */
          .chart-container:has(.settings-panel.open) .chart-container-inner {
            margin-right: 88vw;
          }
          .settings-panel.open ~ .fab-container {
            right: calc(32px + 8vw);
          }
          /* Sticky header already visible; reduce extra padding */
          .filters-container.editing {
            padding-right: 8px;
          }
        }

        @media (max-width: 640px) {
          .filters-container {
            padding: 6px;
          }
          .filter-group {
            gap: 8px;
          }
          .filter-item label {
            font-size: 10px;
          }
          .filter-item select,
          .filter-item input {
            max-width: 180px;
          }
          .action-buttons {
            margin-left: 8px;
            gap: 6px;
          }
        }
      `}</style>
    </>
  );
};

export default ChartComponent;
